import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { tv } from 'tailwind-variants';

import { cn } from '@/libs/utils';
import { IconX } from '@tabler/icons-react';

// Provider and Context remain unchanged
const ToastProvider = ToastPrimitives.Provider;

// ToastViewport with updated ref handling
function ToastViewport({ className, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      className={cn(
        'fixed top-0 right-2 z-100 flex max-h-screen w-full min-w-[300px] flex-col-reverse p-4 sm:flex-col md:max-w-[420px]',
        className
      )}
      {...props}
    />
  );
}
ToastViewport.displayName = 'ToastViewport';

// Toast styling using tailwind-variants
const toastVariants = tv({
  base: 'group pointer-events-auto relative flex w-full items-start justify-between space-x-3 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  variants: {
    variant: {
      default: 'border border-gray-200 bg-background text-gray-900',
      warning: 'border border-yellow-200 bg-yellow-50 text-yellow-900',
      destructive: 'destructive group border-destructive bg-destructive text-destructive-foreground',
      success: 'group border-emerald-600 bg-emerald-600 text-white font-medium',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Toast component with explicit typing
type ToastProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & {
  variant?: 'default' | 'warning' | 'destructive' | 'success';
};

function Toast({ className, variant = 'default', ...props }: ToastProps) {
  return <ToastPrimitives.Root className={cn(toastVariants({ variant }), className)} {...props} />;
}
Toast.displayName = 'Toast';

// ToastAction component with explicit typing
function ToastAction({ className, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>) {
  return (
    <ToastPrimitives.Action
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
        className
      )}
      {...props}
    />
  );
}
ToastAction.displayName = 'ToastAction';

// ToastClose component with explicit typing
function ToastClose({ className, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>) {
  return (
    <ToastPrimitives.Close
      className={cn(
        'absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
        className
      )}
      toast-close=""
      {...props}
    >
      <IconX className="w-4 h-4" />
    </ToastPrimitives.Close>
  );
}
ToastClose.displayName = 'ToastClose';

// ToastTitle component with explicit typing
function ToastTitle({ className, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>) {
  return <ToastPrimitives.Title className={cn('text-sm font-semibold', className)} {...props} />;
}
ToastTitle.displayName = 'ToastTitle';

// ToastDescription component with explicit typing
function ToastDescription({ className, ...props }: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>) {
  return <ToastPrimitives.Description className={cn('text-sm opacity-80 font-base', className)} {...props} />;
}
ToastDescription.displayName = 'ToastDescription';

// Type definition for toast action elements
type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
